// v2/components/CartTable.js — masaustu sepet tablosu
import React, { useCallback, useRef, useEffect, useState } from 'react';
import {
  Package, Minus, Plus, Trash2, Lock, Clock, CheckCircle2, AlertTriangle,
} from 'lucide-react';
import { WAREHOUSES } from '../constants/catalog';
import { formatTRY, formatNumber } from '../utils/format';
import Lightbox from './Lightbox';
import styles from './CartTable.module.css';

/** Basili tutunca hizlanarak tekrar eden aksiyon */
function useHoldRepeat(action) {
  const timer = useRef(null);
  const count = useRef(0);

  const stop = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = null;
    count.current = 0;
  }, []);

  useEffect(() => stop, [stop]);

  const start = useCallback(() => {
    action();
    const tick = () => {
      count.current += 1;
      action();
      timer.current = setTimeout(tick, count.current > 6 ? 40 : 90);
    };
    timer.current = setTimeout(tick, 400);
  }, [action]);

  return {
    onPointerDown: (e) => { if (e.button === 0 || e.pointerType !== 'mouse') start(); },
    onPointerUp: stop,
    onPointerLeave: stop,
    onPointerCancel: stop,
  };
}

/** Degisimde sayarak gecen deger */
function useCountUp(target, duration = 340) {
  const [value, setValue] = useState(target);
  const raf = useRef(null);
  const from = useRef(target);

  useEffect(() => {
    const reduce = typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !Number.isFinite(target)) { setValue(target); from.current = target; return undefined; }
    const start = from.current;
    const delta = target - start;
    if (delta === 0) return undefined;
    const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const tick = (now) => {
      const pr = Math.min((now - t0) / duration, 1);
      setValue(start + delta * (1 - Math.pow(1 - pr, 3)));
      if (pr < 1) raf.current = requestAnimationFrame(tick);
      else from.current = target;
    };
    raf.current = requestAnimationFrame(tick);
    return () => { if (raf.current) cancelAnimationFrame(raf.current); };
  }, [target, duration]);

  return value;
}

/** Urun gorseli + hover onizlemesi */
function Thumb({ product }) {
  const [pos, setPos] = useState(null);
  const [buyut, setBuyut] = useState(false);
  const kareler = (product.images && product.images.length)
    ? product.images
    : (product.image ? [product.image] : []);
  const show = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const top = Math.min(Math.max(r.top - 60, 12), Math.max(window.innerHeight - 250, 12));
    setPos({ top, left: r.right + 12 });
  };
  return (
    <>
      <button
        type="button" className={styles.thumbBtn}
        onMouseEnter={show} onMouseLeave={() => setPos(null)}
        onFocus={show} onBlur={() => setPos(null)}
        onClick={() => { setPos(null); setBuyut(true); }}
        title="Büyütmek için tıklayın"
        aria-label={`${product.description} görselini büyüt`}
      >
        {product.image
          ? <img className={styles.thumbBtnImg} src={product.image} alt="" />
          : <Package size={17} strokeWidth={1.4} />}
      </button>
      {pos && (
        <div className={styles.previewCard} style={{ top: pos.top, left: pos.left }}>
          <div className={styles.previewBox}>
            {product.image
              ? <img className={styles.previewImg} src={product.image} alt="" />
              : <Package size={64} strokeWidth={1.1} />}
          </div>
          <div className={styles.previewName}>{product.description}</div>
          <div className={styles.previewCode}>{product.productCode}</div>
        </div>
      )}
      {buyut && (
        <Lightbox
          kareler={kareler}
          baslik={product.description}
          onClose={() => setBuyut(false)}
        />
      )}
    </>
  );
}

/** Tek depo icin miktar kutusu */
function QtyCell({ line, wh, onQty, locked }) {
  const q = line.qty[wh.key] || 0;
  const stock = line.product[wh.key] || 0;
  const over = q > stock;

  const [raw, setRaw] = useState(String(q));
  useEffect(() => { setRaw(String(q)); }, [q]);

  const dec = useCallback(() => onQty(line.key, wh.key, Math.max(q - 1, 0)), [line.key, wh.key, q, onQty]);
  const inc = useCallback(() => onQty(line.key, wh.key, q + 1), [line.key, wh.key, q, onQty]);
  const holdDec = useHoldRepeat(dec);
  const holdInc = useHoldRepeat(inc);

  if (locked) {
    return <span className={styles.qtyLocked}>{q}</span>;
  }

  const commit = () => {
    const n = Math.max(parseInt(raw, 10) || 0, 0);
    setRaw(String(n));
    onQty(line.key, wh.key, n);
  };

  return (
    <span
      className={`${styles.qtyBox} ${q > 0 ? styles.qtyOn : ''} ${over ? styles.qtyOver : ''}`}
      title={over ? `Stokta ${formatNumber(stock)} adet var` : `${wh.city}: ${formatNumber(stock)} adet stokta`}
    >
      <button type="button" className={styles.qtyBtn} {...holdDec}
              disabled={q <= 0} aria-label={`${wh.city} azalt`}>
        <Minus size={12} />
      </button>
      <input
        className={styles.qtyInput} type="text" inputMode="numeric"
        value={raw} onChange={(e) => {
          const v = e.target.value;
          if (v === '' || /^[0-9]{0,5}$/.test(v)) setRaw(v);
        }}
        onBlur={commit} onFocus={(e) => e.target.select()}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        aria-label={`${wh.city} adet`}
      />
      <button type="button" className={styles.qtyBtn} {...holdInc}
              aria-label={`${wh.city} artır`}>
        <Plus size={12} />
      </button>
    </span>
  );
}

/** Tek satir */
function Row({ line, selected, onSelect, onQty, onRemove, eurRate, lineUnits }) {
  const p = line.product;
  const locked = line.status !== 'pending';
  const islemde = line.status === 'processing';
  const units = lineUnits(line);
  const total = useCountUp(p.netPrice * units);
  const gross = p.grossPrice * units;
  const save = useCountUp(p.savingAmount * units);

  const over = WAREHOUSES.some((w) => (line.qty[w.key] || 0) > (p[w.key] || 0));

  return (
    <tr className={`${styles.row} ${locked ? styles.rowLocked : ''} ${over ? styles.rowOver : ''}`}>
      <td className={styles.cSel}>
        {locked ? (
          <span className={styles.lockIcon}
                title={islemde
                  ? 'Siparişi verildi — düzenlenemez'
                  : 'Transfer edildi — düzenlenemez'}>
            <Lock size={13} />
          </span>
        ) : (
          <input
            type="checkbox" className={styles.check}
            checked={selected} onChange={() => onSelect(line.key)}
            aria-label={`${p.description} seç`}
          />
        )}
      </td>

      <td className={styles.cImg}>
        <Thumb product={p} />
      </td>

      <td className={styles.cCode}>
        <span className={styles.code}>{p.productCode}</span>
      </td>

      <td className={styles.cName}>
        <span className={styles.nameWrap}>
          <span className={styles.name} title={p.description}>{p.description}</span>
        </span>
      </td>

      {WAREHOUSES.map((w) => (
        <td key={w.key} className={styles.cQty}>
          <QtyCell line={line} wh={w} onQty={onQty} locked={locked} />
        </td>
      ))}

      <td className={styles.cPrice}>
        <span className={styles.grossVal}>{formatTRY(gross)}</span>
      </td>
      <td className={styles.cChain}>
        <span className={styles.chips}>
          {p.discounts.map((d, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span className={styles.arrow}>›</span>}
              <span className={`${styles.chip} ${d.kind === 'general' ? styles.chipCamp : ''}`}>
                %{formatNumber(d.rate, 2)}
              </span>
            </React.Fragment>
          ))}
        </span>
      </td>

      <td className={styles.cPrice}>
        <span className={styles.netVal}>{formatTRY(p.netPrice)}</span>
      </td>

      <td className={styles.cTotal}>
        <span className={styles.totalWrap}>
          <span className={styles.totalVal}>{formatTRY(total)}</span>
          <span className={styles.saveVal}>+{formatTRY(save)} kazanç</span>
        </span>
      </td>

      <td className={styles.cStatus}>
        {islemde ? (
          <span className={`${styles.status} ${styles.statusProc}`}>
            <Clock size={12} /> İşlemde
          </span>
        ) : locked ? (
          <span className={`${styles.status} ${styles.statusDone}`}>
            <CheckCircle2 size={12} /> Transfer Edildi
          </span>
        ) : over ? (
          <span className={`${styles.status} ${styles.statusWarn}`}>
            <AlertTriangle size={12} /> Stok Aşımı
          </span>
        ) : (
          <span className={`${styles.status} ${styles.statusWait}`}>
            <Clock size={12} /> Beklemede
          </span>
        )}
      </td>

      <td className={styles.cAct}>
        {!locked && (
          <button type="button" className={styles.del}
                  onClick={() => onRemove(line.key)}
                  aria-label={`${p.description} kaldır`}>
            <Trash2 size={14} />
          </button>
        )}
      </td>
    </tr>
  );
}

export default function CartTable({
  lines, selected, onSelect, onSelectAll,
  onQty, onRemove, eurRate = 0, lineUnits,
}) {
  const selectable = lines.filter((l) => l.status === 'pending');
  const allOn = selectable.length > 0 && selectable.every((l) => selected.has(l.key));

  return (
    <div className={styles.scroll}>
      <table className={styles.table}>
        <colgroup>
          <col className={styles.colSel} />
          <col className={styles.colImg} />
          <col className={styles.colCode} />
          <col className={styles.colName} />
          {WAREHOUSES.map((w) => <col key={w.key} className={styles.colQty} />)}
          <col className={styles.colGross} />
          <col className={styles.colChain} />
          <col className={styles.colNet} />
          <col className={styles.colTotal} />
          <col className={styles.colStatus} />
          <col className={styles.colAct} />
        </colgroup>
        <thead>
          <tr>
            <th className={styles.cSel}>
              <input
                type="checkbox" className={styles.check}
                checked={allOn} onChange={onSelectAll}
                disabled={selectable.length === 0}
                aria-label="Tümünü seç"
              />
            </th>
            <th className={styles.cImg} />
            <th className={styles.cCode}>Ürün Kodu</th>
            <th className={styles.cName}>Ürün Adı</th>
            {WAREHOUSES.map((w) => (
              <th key={w.key} className={styles.cQty}>{w.city}</th>
            ))}
            <th className={styles.cPrice}>Brüt Fiyat</th>
            <th className={styles.cChain}>İndirim</th>
            <th className={styles.cPrice}>Net Fiyat</th>
            <th className={styles.cTotal}>Toplam</th>
            <th className={styles.cStatus}>Durum</th>
            <th className={styles.cAct}>İşlem</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <Row
              key={l.key} line={l}
              selected={selected.has(l.key)} onSelect={onSelect}
              onQty={onQty} onRemove={onRemove}
              eurRate={eurRate} lineUnits={lineUnits}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
